import React, { useEffect } from "react";
import { ThemeBasedColorHeader, ThemeBasedColorMenubar } from "src/shared/modules/profile/theme/themeCheacker";
import { Order, OrderedProduct, ProductReturnItem } from "src/shared/types";

interface Props {
  order: Order | null;
  returnQty: Record<number, number>;
  setReturnQty: React.Dispatch<React.SetStateAction<Record<number, number>>>;
}

export default function OrderItemsTable({ order, returnQty, setReturnQty }: Props) {
  const [returnProduct, setReturnProduct] = React.useState<ProductReturnItem[]>([]);

  const handleQtyChange = (i: number, value: string, max: number) => {
    const qty = Math.max(0, Math.min(Number(value) || 0, max));

    setReturnQty((prev) => ({
      ...prev,
      [i]: qty,
    }));
  };

  useEffect(() => {
    if (!order) return;

    const returnItems = order?.orderDetails?.filter((detail) => detail.paymentSource === "RETURN") || [];

    const returnedProducts = returnItems.flatMap((detail) => detail.products) ?? [];

    setReturnProduct(returnedProducts);
  }, [order]);

  // Calculate returned quantity product-wise
  const getReturnedQty = (productId: string): number => {
    return Math.abs(returnProduct.filter((p) => p.productId === productId).reduce((sum, p) => sum + Number(p.quantity || 0), 0));
  };

  if (!order) {
    return <div className={`col-span-3 ${ThemeBasedColorMenubar()} rounded-xl shadow p-10 text-center text-gray-400`}>No Data</div>;
  }

  // Main ordered products
  const products: OrderedProduct[] = order.orderDetails?.[0]?.products ?? [];

  return (
    <div className={`col-span-4 rounded-xl shadow p-2 border ${ThemeBasedColorMenubar()}`}>
      <h2 className="text-lg font-semibold mb-3">Order Items</h2>

      <table className="w-full text-sm">
        <thead className={`${ThemeBasedColorHeader()} text-white`}>
          <tr>
            {["S.No", "Product", "Price", "Qty", "Returned", "Return Qty", "Total"].map((h) => (
              <th key={h} className="p-2">
                {h}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {products.map((p, i) => {
            const qty = returnQty[i] || 0;

            const returned = getReturnedQty(p.productId);

            const maxReturn = Math.max(0, p.quantity - returned);

            return (
              <tr key={i} className="text-center border-b bg-white">
                <td className="p-2">{i + 1}</td>

                <td className="p-2">
                  {p.name}

                  {p.arabicName ? (
                    <>
                      <br />
                      {p.arabicName}
                    </>
                  ) : null}
                </td>

                <td className="p-2">{Number(p.unitPrice).toFixed(2)}</td>

                <td className="p-2">{p.quantity}</td>

                <td className="p-2 text-red-500 font-medium">{returned}</td>

                <td className="p-2">
                  <input
                    type="number"
                    value={qty || ""}
                    min={0}
                    max={maxReturn}
                    onChange={(e) => handleQtyChange(i, e.target.value, maxReturn)}
                    placeholder="0"
                    className="border w-16 text-center rounded placeholder:text-gray-700"
                    disabled={maxReturn === 0}
                  />
                </td>

                <td className="p-2 font-medium">{(qty * Number(p.unitPrice)).toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
